/* ── RapTV Admin Main Entry Point (ES Module) ── */

import './core/sync-epoch.js';
import * as G from './admin-globals.js';
import * as UI from './admin-ui-base.js';
import * as Auth from './admin-auth.js';
import * as CatalogAPI from './catalog-api.js';
import * as CatalogUI from './catalog-ui.js';
import * as YT from './admin-yt.js';
import * as Search from './admin-search.js';
import * as PlaylistImport from './admin-playlist-import.js';
import * as Library from './admin-library.js';
import * as Minio from './admin-minio.js';
import * as Preview from './admin-preview.js';
import * as CatalogMod from './admin/catalog.js';
import * as UsersMod from './admin/users.js';
import * as VideoMgr from './admin/video-manager.js';
import * as ChannelMgr from './admin/channel-manager.js';
import * as Watch from './watch-modal.js';
import { toggleScheduleView } from './ui/schedule-manager.js';
import { openAgenda, closeAgenda } from './ui/schedule-renderer.js';
import { fetchMissingDurations } from './duration-service.js';

// ── 1. IMMEDIATE FUNCTION BRIDGE ──
console.log("[RAPTV] Registering global bridge...");
if (typeof window.pendingFetchId === 'undefined') window.pendingFetchId = null;

// Callback SIEMPRE antes de cargar iframe_api — el script inline/defer perdía la carrera con el módulo ES.
window.onYouTubeIframeAPIReady = YT.onYouTubeIframeAPIReady;
if (!document.getElementById('raptv-yt-iframe-api')) {
  const tag = document.createElement('script');
  tag.id = 'raptv-yt-iframe-api';
  tag.src = 'https://www.youtube.com/iframe_api';
  tag.async = true;
  document.head.appendChild(tag);
}

// Services
window.fetchMissingDurations = fetchMissingDurations;

// YT & Search
window.openYTSearch = Search.openYTSearch;
window.closeYTSearch = Search.closeYTSearch;
window.searchYouTube = Search.searchYouTube;
window.fetchYTDuration = Search.fetchYTDuration;
window.openPlaylistImport = PlaylistImport.openPlaylistImport;
window.closePlaylistImport = PlaylistImport.closePlaylistImport;
window.submitPlaylistImport = PlaylistImport.submitPlaylistImport;
window.openLibraryModal = Library.openLibraryModal;
window.closeLibraryModal = Library.closeLibraryModal;
window.onLibrarySearchInput = Library.onLibrarySearchInput;
window.searchLibrary = Library.searchLibrary;
window.addFromLibrary = Library.addFromLibrary;
window.previewFromLibraryIndex = Library.previewFromLibraryIndex;

// Schedule
window.toggleScheduleView = toggleScheduleView;
window.openAgenda = openAgenda;
window.closeAgenda = closeAgenda;

// Minio
window.openMinioModal = Minio.openMinioModal;
window.closeMinioModal = Minio.closeMinioModal;
window.onMinioSearchInput = Minio.onMinioSearchInput;
window.fetchMinioDuration = Minio.fetchMinioDuration;
window.onMinioUpload = Minio.onMinioUpload;
window.onMinioImportUrl = Minio.onMinioImportUrl;

// Auth & UI
window.checkLogin = Auth.checkLogin;
window.logout = Auth.logout;
window.checkAuth = Auth.checkAuth;
window.authHeaders = Auth.authHeaders;
window.showToast = UI.showToast;
window.showConfirm = UI.showConfirm;
window.escHtml = UI.escHtml;

// Catalog logic (Modules)
window.selectChannel = CatalogUI.selectChannel;
window.renderSidebar = CatalogUI.renderSidebar;
window.renderMain = CatalogUI.renderMain;
window.renderVideoList = CatalogUI.renderVideoList;
window.loadCatalog = CatalogAPI.loadCatalog;

// Preview (Module)
window.previewVideo = Preview.previewVideo;
window.previewMinioFile = Preview.previewMinioFile;
window.closePreview = Preview.closePreview;

// User Management Bridge
window.openUsersModal = UsersMod.openUsersModal;

// Video Manager Bridge
window.openAddVideo = VideoMgr.openAddVideo;
window.openEditVideo = VideoMgr.openEditVideo;
window.closeVideoModal = VideoMgr.closeVideoModal;
window.onTypeChange = VideoMgr.onTypeChange;

// Watch Modal Bridge
window.openWatchModal = Watch.openWatchModal;
window.closeWatchModal = Watch.closeWatchModal;
window.changeWatchChannel = Watch.changeWatchChannel;
window.toggleWatchMute = Watch.toggleWatchMute;
window.toggleWatchFullscreen = Watch.toggleWatchFullscreen;
window.openSingleVideoPreview = Watch.openSingleVideoPreview;

window.saveVideo = VideoMgr.saveVideo;
window.shuffleVideos = VideoMgr.shuffleVideos;
window.deleteVideo = VideoMgr.deleteVideo;
window.clearVideos = VideoMgr.clearVideos;
window.moveVideo = VideoMgr.moveVideo;
window.toggleFlag = VideoMgr.toggleFlag;
window.moveToQuarantine = VideoMgr.moveToQuarantine;
window.restoreFromQuarantine = VideoMgr.restoreFromQuarantine;
window.setMoveChannelUi = VideoMgr.setMoveChannelUi;
window.openMoveVideoToChannel = VideoMgr.openMoveVideoToChannel;
window.closeMoveVideoToChannelModal = VideoMgr.closeMoveVideoToChannelModal;

// Channel Manager Bridge
window.openAddChannel = ChannelMgr.openAddChannel;
window.closeChannelModal = ChannelMgr.closeChannelModal;
window.updateChannelField = ChannelMgr.updateChannelField;
window.importChannelJson = ChannelMgr.importChannelJson;
window.deleteChannel = ChannelMgr.deleteChannel;
window.saveChannel = ChannelMgr.saveChannel;

// Catalog Actions Bridge
let catalogActions = {};
const updateCatalogBridge = () => {
  catalogActions = CatalogMod.initCatalogActions(G, window.renderVideoList, fetchMissingDurations);
  
  window.exportChannel = catalogActions.exportChannel;
  window.importChannel = catalogActions.importChannel;
  window.shareChannel = catalogActions.shareChannel;
  window.syncMinioTags = catalogActions.syncMinioTags;
  window.saveCatalog = catalogActions.saveCatalog;
};

// Initialize catalog bridge
updateCatalogBridge();

// ── 2. REACTIVE STATE BRIDGE ──
const bridgeState = (key) => {
  Object.defineProperty(window, key, {
    get: () => G[key],
    set: (v) => G.updateState(key, v),
    configurable: true
  });
};

const ALL_GLOBALS = [
  'catalog', 'channels', 'selectedChannel', 'currentUser', 
  'isAdminMode', 'ytFetcher', 'ytFetcherReady',
  '_scheduleVisible', '_minioPrefix', '_minioAllTags',
  'editingVideoIndex', 'originalVideoSnapshot', 'returnToSearch',
  '_scheduleTimer', '_scheduleNowMap', 'onDurationFetched'
];

ALL_GLOBALS.forEach(bridgeState);

// ── 3. INITIALIZATION ──
document.addEventListener('DOMContentLoaded', async () => {
  console.log("[RAPTV] Admin Module System Initialized (v9 - Quarantine Ready)");
  
  // Load dynamic CRT templates first
  try {
    const res = await fetch('/channels/info/templates');
    if (res.ok) {
      const templates = await res.json();
      G.updateState('templates', templates);
      console.log(`[RAPTV] Loaded ${templates.length} CRT templates`);
    }
  } catch (e) {
    console.error("[RAPTV] Failed to load templates signal:", e);
  }

  Auth.checkAuth();
  VideoMgr.initMoveVideoChannelModalBackdrop();
});

export { G, UI, Auth, CatalogAPI, CatalogUI, YT, Search, Minio, Preview, CatalogMod, UsersMod, VideoMgr, ChannelMgr };
